import { memo, type ReactNode } from 'react';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import classNames from '@/utils/classnames';

import { ColorSwatch, isColorLiteral } from './ColorSwatch';
import { MermaidDiagram } from './MermaidDiagram';

/**
 * Renders assistant content as GitHub-flavored markdown, mapped to the Zooid design tokens
 * (spec §2/§3): sans body text in `fg-secondary`, mono only inside code, `surface-2` for code
 * surfaces. User turns stay plain text — this is assistant-only.
 *
 * Two variants, because the same markdown is read in two very different places:
 *
 * - `chat` (default) is a message in a bubble. Compact, tight rhythm, headings only a step above
 *   body text — a chat reply is a paragraph or two and a big `#` would shout.
 * - `document` is the plan pane: a page-length document the reader scans for structure. It gets a
 *   real typographic ramp and section rules, because the failure mode there is the opposite one —
 *   with chat-sized headings an `h2` and an `h3` are indistinguishable and the outline disappears.
 */
export type MarkdownVariant = 'chat' | 'document';

/** Pull the plain text out of a react-markdown children tree, for pattern-matching a code span. */
function textOf(children: ReactNode): string {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(textOf).join('');
  return '';
}

const LANGUAGE_CLASS = /language-(\w+)/;
const TASK_LIST_CLASS = /contains-task-list/;
const TASK_LIST_ITEM_CLASS = /task-list-item/;

/** The language of a fenced block, from react-markdown's `language-xxx` class. */
function languageOf(className: string | undefined): string | null {
  return LANGUAGE_CLASS.exec(className ?? '')?.[1] ?? null;
}

function buildComponents(variant: MarkdownVariant): Components {
  const doc = variant === 'document';

  return {
    p: ({ children }) => (
      <p className={doc ? 'text-sm leading-[1.65] text-fg-secondary' : 'text-sm leading-[1.55] text-fg-secondary'}>
        {children}
      </p>
    ),

    // ── Headings ────────────────────────────────────────────────────────
    // The document ramp is deliberately wide — size, weight, colour and a rule all move together,
    // so the level of a heading is legible at a glance without reading it.
    h1: ({ children }) =>
      doc ? (
        <h1 className="mt-1 border-b border-line pb-2 text-xl font-bold leading-tight tracking-[-0.02em] text-fg">
          {children}
        </h1>
      ) : (
        <h1 className="text-base font-semibold tracking-[-0.01em] text-fg">{children}</h1>
      ),
    h2: ({ children }) =>
      doc ? (
        // The rule above is what turns a long plan into scannable sections.
        <h2 className="mt-5 flex items-center gap-2.5 border-t border-line pt-4 text-base font-semibold tracking-[-0.01em] text-fg">
          {children}
        </h2>
      ) : (
        <h2 className="text-sm font-semibold tracking-[-0.005em] text-fg">{children}</h2>
      ),
    h3: ({ children }) =>
      doc ? (
        // An accent bar rather than another size step: h3 is close enough to h2 in scale that
        // colour and a marker separate them more clearly than half a rem would.
        <h3 className="mt-3 flex items-center gap-2 text-sm font-semibold text-fg">
          <span aria-hidden className="h-3.5 w-0.75 shrink-0 rounded-full bg-brand" />
          {children}
        </h3>
      ) : (
        <h3 className="text-sm font-semibold text-fg">{children}</h3>
      ),
    h4: ({ children }) =>
      doc ? (
        <h4 className="mt-2 text-2xs font-semibold uppercase tracking-[0.06em] text-fg-tertiary">{children}</h4>
      ) : (
        <h4 className="text-sm font-semibold text-fg">{children}</h4>
      ),

    a: ({ children, href }) => (
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className="font-medium text-brand-text underline decoration-line-strong underline-offset-2 hover:text-brand-hover"
      >
        {children}
      </a>
    ),

    // ── Lists ───────────────────────────────────────────────────────────
    // `list-none` when GFM produced a task list: the checkbox is the marker, and a bullet
    // alongside it reads as a stray dot.
    ul: ({ children, className }) => {
      const isTaskList = TASK_LIST_CLASS.test(className ?? '');
      return (
        <ul
          className={classNames(
            'flex flex-col text-sm leading-[1.55] text-fg-secondary marker:text-fg-tertiary',
            doc ? 'gap-1.5' : 'gap-1',
            isTaskList ? 'list-none pl-0.5' : 'list-disc pl-5',
          )}
        >
          {children}
        </ul>
      );
    },
    ol: ({ children }) => (
      <ol
        className={classNames(
          'flex list-decimal flex-col pl-5 text-sm leading-[1.55] text-fg-secondary marker:text-fg-tertiary',
          doc ? 'gap-1.5' : 'gap-1',
        )}
      >
        {children}
      </ol>
    ),
    li: ({ children, className }) => (
      <li className={classNames('pl-0.5', TASK_LIST_ITEM_CLASS.test(className ?? '') && 'flex items-start gap-2')}>
        {children}
      </li>
    ),
    // GFM renders `- [x]` as a disabled checkbox. Restyled rather than left as a raw OS control,
    // and kept disabled — this is a rendered document, not a form.
    input: ({ checked, type }) =>
      type === 'checkbox' ? (
        <input
          type="checkbox"
          checked={checked}
          readOnly
          className="mt-1 size-3.5 shrink-0 cursor-default accent-brand"
        />
      ) : null,

    strong: ({ children }) => <strong className="font-semibold text-fg">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,

    blockquote: ({ children }) =>
      doc ? (
        // A callout in a document, not a quotation: plans use `>` for notes and caveats, and an
        // italic grey quote makes the one thing the author wanted to stand out recede instead.
        <blockquote className="rounded-r-md border-l-3 border-brand bg-surface-2 px-3 py-2 text-sm text-fg-secondary [&_p]:text-fg-secondary">
          {children}
        </blockquote>
      ) : (
        <blockquote className="border-l-2 border-line-strong pl-3 text-sm italic text-fg-tertiary">
          {children}
        </blockquote>
      ),

    hr: () => <hr className="border-line" />,

    // ── Code ────────────────────────────────────────────────────────────
    // `pre` only wraps the non-mermaid case: a mermaid fence is swapped for a diagram at the
    // `code` level, and wrapping that in a `pre` would put a scroll box around an SVG.
    pre: ({ children }) => {
      const child = Array.isArray(children) ? children[0] : children;
      const props = (child as { props?: { className?: string; children?: ReactNode } })?.props;
      if (languageOf(props?.className) === 'mermaid') return <>{children}</>;

      const language = languageOf(props?.className);
      return (
        <div className="overflow-hidden rounded-md border border-line">
          {doc && language && (
            <div className="border-b border-line bg-surface-2 px-3 py-1 font-mono text-2xs text-fg-tertiary">
              {language}
            </div>
          )}
          <pre className="overflow-x-auto bg-surface-2 px-3 py-2.5 font-mono text-2xs leading-relaxed text-fg-secondary">
            {children}
          </pre>
        </div>
      );
    },
    code: ({ className, children }) => {
      const text = textOf(children);
      const language = languageOf(className);

      if (language === 'mermaid') return <MermaidDiagram code={text.trimEnd()} />;

      const isBlock = language !== null || text.includes('\n');
      if (isBlock) return <code className={classNames('font-mono', className)}>{children}</code>;

      // A colour literal gets its colour shown next to it — the whole point of a palette table.
      if (isColorLiteral(text)) {
        return (
          <code className="inline-flex items-center gap-1.5 rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[0.85em] text-fg">
            <ColorSwatch value={text.trim()} />
            {children}
          </code>
        );
      }
      return <code className="rounded bg-surface-2 px-1 py-0.5 font-mono text-[0.85em] text-fg">{children}</code>;
    },

    // ── Tables ──────────────────────────────────────────────────────────
    table: ({ children }) => (
      <div className="overflow-x-auto rounded-lg border border-line">
        <table className="w-full border-collapse text-xs">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-surface-2">{children}</thead>,
    // Zebra striping in the document variant: palette and spec tables run long, and alternating
    // rows are what keep a value on the right-hand side attached to its token on the left.
    tbody: ({ children }) => (
      <tbody className={doc ? '[&>tr:nth-child(even)]:bg-surface-2/40' : undefined}>{children}</tbody>
    ),
    th: ({ children }) => (
      <th className="border-b border-line px-2.5 py-1.5 text-left font-semibold text-fg">{children}</th>
    ),
    td: ({ children }) => (
      <td className="border-b border-line px-2.5 py-1.5 align-middle text-fg-secondary">{children}</td>
    ),
  };
}

const CHAT_COMPONENTS = buildComponents('chat');
const DOCUMENT_COMPONENTS = buildComponents('document');

function AssistantMarkdownImpl({ content, variant = 'chat' }: { content: string; variant?: MarkdownVariant }) {
  const doc = variant === 'document';
  return (
    <div className={classNames('flex flex-col', doc ? 'gap-3' : 'gap-2')}>
      <Markdown remarkPlugins={[remarkGfm]} components={doc ? DOCUMENT_COMPONENTS : CHAT_COMPONENTS}>
        {content}
      </Markdown>
    </div>
  );
}

export const AssistantMarkdown = memo(AssistantMarkdownImpl);
